import { ClientEvents, MealPlanAnalyticsLocation } from 'common/events/ClientEvents';
import { getWSSPricePerServing } from 'common/meal-plan/ConsumerPrices';
import { getServingsPerMealByFoodType, isMealPlanMain, isMealPlanSideOrStaple } from 'common/MealPlanUtils';
import { MealPlanDisplayFoodItemFragment, MealPlanFormat, MealPlanShefFragment } from 'src/gqlReactTypings.generated.d';
import { getQuantityInUnits } from 'src/pages/consumer/meal-plans/utils/cartServingsConversion';
import { useIsFlexServingsV3 } from 'src/shared/hooks/experiments/useIsFlexServingsV3';
import { useIsUserInFlexServings } from 'src/shared/hooks/experiments/useIsUserInFlexServings';
import { useLocation } from 'src/shared/hooks/useLocation';
import { useTracker } from 'src/shared/hooks/useTracker';
import { Routes } from 'src/shared/Routes';
import { locationInRoutes } from 'src/shared/utils/RouteUtilities';
import { useCartStore, useCartStoreSelectors } from 'src/ztore/cart-store';
import { useFoodItemAvailability } from 'src/ztore/wss/hooks/useFoodItemAvailability';
import { useMealPlanDeliveryInfo } from './useMealPlanDeliveryInfo';

interface UseCartActionsWithAnalyticsParams {
  analyticsLocation: MealPlanAnalyticsLocation;
  shef: MealPlanShefFragment;
  foodItem: MealPlanDisplayFoodItemFragment;
  shefPosition?: number;
  foodItemPosition?: number;
  collectionId?: string;
}

interface UseCartActionsWithAnalyticsReturn {
  incrementInCart: () => void;
  decrementInCart: () => void;
  setQuantityInCart: (quantity: number) => void;
  setServingsInCart: (servings: number) => void;
  /** Servings a flex main's first add puts in the cart: a full meal on Flex V2, one on Flex V3. */
  flexFirstAddServings: number;
}

/**
 * Hook to handle all cart actions adding or removing dishes from the meal plan cart-store
 * and track the actions in analytics.
 *
 * @param analyticsLocation: MealPlanAnalyticsLocation - location the card is rendered for analytics
 * @param shef: MealPlanShefFragment - shef required for the cart store, card display and analytics
 * @param foodItem: MealPlanDisplayFoodItemFragment - food item to render
 * @param shefPosition?: number - position of the shef on the page
 * @param foodItemPosition?: number - position of the food item within the shef display or collection
 * @param collectionId?: string - collection id for analytics
 * @returns {
 *  onIncrement: () => void,
 *  onDecrement: () => void,
 *  setQuantity: (quantity: number) => void,
 * }
 */
export const useCartActionsWithAnalytics = ({
  analyticsLocation,
  shef,
  foodItem,
  shefPosition = 0,
  foodItemPosition = 0,
  collectionId,
}: UseCartActionsWithAnalyticsParams): UseCartActionsWithAnalyticsReturn => {
  const tracker = useTracker();
  const location = useLocation();
  const {
    cart,
    numServings,
    mealsPerWeek: storeMealsPerWeek,
    getNumSelectedServings,
    increaseCartQuantity,
    decreaseCartQuantity,
    setItemQuantity,
  } = useCartStoreSelectors();
  const requiredServings = (storeMealsPerWeek ?? 0) * numServings;
  const isFlexServings = useIsUserInFlexServings();
  const isFlexServingsV3 = useIsFlexServingsV3();
  const { numServingsInCart, numAvailableServings } = useFoodItemAvailability(foodItem, shef);
  const { regionId } = useMealPlanDeliveryInfo();
  const isMain = isMealPlanMain(foodItem);
  const servingsPerMeal = getServingsPerMealByFoodType(foodItem.mealPlanFoodType, numServings) || 1;
  const dishSelectionRoute = locationInRoutes(location, [Routes.CONSUMER_MEAL_PLANS_EDIT_ORDER]) ? 'edit' : 'checkout';

  /**
   * Helper: analytics tracking when adding a foodItem to cart.
   * There are different events tracked for main dishes vs. side/staple dishes.
   */
  const trackAdd = (flexServing: boolean, numSelectedServings: number) => {
    const { id: foodItemId } = foodItem;
    const isSideFoodItem = isMealPlanSideOrStaple(foodItem);

    if (isSideFoodItem) {
      tracker.track(ClientEvents.MEAL_PLANNING_MVP_SIDE_OR_STAPLE_ADD, {
        foodItemId: String(foodItemId),
        path: dishSelectionRoute,
        location: analyticsLocation,
        format: MealPlanFormat.FlexibleSides,
        collectionId,
      });
    } else {
      tracker.track(ClientEvents.MEAL_PLANNING_MVP_ADD_TO_CART, {
        foodItemId: String(foodItemId),
        shefId: shef.id,
        shefPosition,
        foodItemPosition,
        path: dishSelectionRoute,
        location: analyticsLocation,
        // These are for CIO tracking
        foodItemDescription: foodItem.description ?? '',
        foodItemName: foodItem.name ?? '',
        foodItemImageUrl: foodItem.imageUrl ?? '',
        shefName: foodItem.shef.firstName ?? foodItem.shef.publicName,
        collectionId,
        format: MealPlanFormat.FlexibleSides,
        flexServing,
        numSelectedServings,
        requiredServings,
        mealPlanFoodType: foodItem.mealPlanFoodType,
        foodItemPrice: getWSSPricePerServing(foodItem, regionId),
      });
    }
  };

  /**
   * Callers track *after* mutating the cart, and `getNumSelectedServings` reads live store
   * state, so `numSelectedServings` describes the cart the user now sees rather than the one
   * they clicked on. Funnel analysis wants the resulting depth, and it keeps the property
   * consistent across the four entry points, which compute their deltas differently.
   */
  const trackCartChange = (action: 'add' | 'remove', flexServing = false) => {
    const numSelectedServings = getNumSelectedServings();
    switch (action) {
      case 'add':
        trackAdd(flexServing, numSelectedServings);
        break;
      case 'remove':
        tracker.track(ClientEvents.MEAL_PLANNING_MVP_REMOVE_FROM_CART, {
          foodItemId: String(foodItem.id),
          shefId: shef.id,
          shefPosition,
          foodItemPosition,
          path: dishSelectionRoute,
          location: analyticsLocation,
          format: MealPlanFormat.FlexibleSides,
          collectionId,
          numSelectedServings,
          requiredServings,
          mealPlanFoodType: foodItem.mealPlanFoodType,
        });
        break;
      default:
        break;
    }
  };

  const isFlexMain = isFlexServings && isMain;
  const flexFirstAddServings = isFlexServingsV3 ? 1 : servingsPerMeal;

  const incrementInCart = () => {
    if (isFlexMain) {
      // Re-read the cart at click time so rapid clicks clamp against the live
      // count rather than a stale render-time snapshot and overshoot capacity.
      const capacity = numServingsInCart + numAvailableServings;
      const liveItem = useCartStore
        .getState()
        .cart[shef.id]?.cartItems.find((item) => item.foodItem.id === foodItem.id);
      const currentServings = liveItem ? liveItem.quantity : 0;
      const desiredServings = currentServings <= 0 ? flexFirstAddServings : 1;
      const servingsToAdd = Math.min(desiredServings, capacity - currentServings);
      if (servingsToAdd <= 0) return;
      // The add includes an extra ("flex") serving when it pushes the cart's total main
      // servings past the plan minimum — including a multi-serving add that straddles it.
      const isFlexServing = requiredServings > 0 && getNumSelectedServings() + servingsToAdd > requiredServings;
      increaseCartQuantity({ foodItem, quantity: servingsToAdd }, shef);
      trackCartChange('add', isFlexServing);
      return;
    }
    increaseCartQuantity({ foodItem, quantity: servingsPerMeal }, shef);
    trackCartChange('add');
  };

  const decrementInCart = () => {
    const servingsToRemove = isFlexMain ? 1 : servingsPerMeal;
    decreaseCartQuantity({ foodItem, quantity: servingsToRemove });
    trackCartChange('remove');
  };

  const setQuantityInCart = (quantity: number) => {
    const itemInCart = cart[shef.id]?.cartItems.find((item) => item.foodItem.id === foodItem.id);
    const previousUnits = itemInCart ? getQuantityInUnits(itemInCart, numServings) : 0;

    setItemQuantity({ foodItem, quantity: quantity * servingsPerMeal }, shef);
    trackCartChange(previousUnits < quantity ? 'add' : 'remove');
  };

  const setServingsInCart = (servings: number) => {
    const itemInCart = cart[shef.id]?.cartItems.find((item) => item.foodItem.id === foodItem.id);
    const previousServings = itemInCart ? itemInCart.quantity : 0;
    const isFlexServing =
      requiredServings > 0 && getNumSelectedServings() + servings - previousServings > requiredServings;

    setItemQuantity({ foodItem, quantity: servings }, shef);
    trackCartChange(previousServings < servings ? 'add' : 'remove', isFlexServing);
  };

  return {
    incrementInCart,
    decrementInCart,
    setQuantityInCart,
    setServingsInCart,
    flexFirstAddServings,
  };
};
